import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import {
    consolidationBasisOptionsBodySchema,
    consolidationBasisOptionsResultSchema,
    consolidationCanvasLayoutSchema,
    consolidationEligibilityBodySchema,
    consolidationEligibilityResultSchema,
    consolidationGroupMemberSchema,
    consolidationGroupSchema,
    consolidationRelationshipSchema,
    createConsolidationGroupBodySchema,
    createConsolidationRelationshipBodySchema,
    listConsolidationRelationshipsQuerySchema,
    updateConsolidationCanvasLayoutBodySchema,
    updateConsolidationGroupMemberBodySchema,
    updateConsolidationGroupConfigBodySchema,
    updateConsolidationRelationshipBodySchema,
} from "@common/schemas/domains/accounting/consolidation.schema";
import {
    createEliminatingEntryBodySchema,
    eliminatingEntryMutationResponseSchema,
    listEliminatingEntriesResponseSchema,
    updateEliminatingEntryBodySchema,
} from "@common/schemas/domains/accounting/eliminatingEntry.schema";

const c = initContract();

const workspacePathParams = z.object({
    workspaceId: z.string().uuid(),
});

const workspaceResourcePathParams = z.object({
    workspaceId: z.string().uuid(),
    id: z.string().uuid(),
});

const groupEliminatingEntryPathParams = z.object({
    workspaceId: z.string().uuid(),
    groupId: z.string().uuid(),
});

const groupEliminatingEntryResourcePathParams = z.object({
    workspaceId: z.string().uuid(),
    groupId: z.string().uuid(),
    id: z.string().uuid(),
});

export const consolidationContract = c.router(
    {
        listRelationships: {
            method: "GET",
            path: "/:workspaceId/relationships",
            pathParams: workspacePathParams,
            query: listConsolidationRelationshipsQuerySchema,
            responses: {
                200: z.object({
                    relationships: z.array(consolidationRelationshipSchema),
                }),
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createRelationship: {
            method: "POST",
            path: "/:workspaceId/relationships",
            pathParams: workspacePathParams,
            body: createConsolidationRelationshipBodySchema,
            responses: {
                201: z.object({
                    relationship: consolidationRelationshipSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateRelationship: {
            method: "PATCH",
            path: "/:workspaceId/relationships/:id",
            pathParams: workspaceResourcePathParams,
            body: updateConsolidationRelationshipBodySchema,
            responses: {
                200: z.object({
                    relationship: consolidationRelationshipSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        deleteRelationship: {
            method: "DELETE",
            path: "/:workspaceId/relationships/:id",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: z.void(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        getGroups: {
            method: "GET",
            path: "/:workspaceId/groups",
            pathParams: workspacePathParams,
            responses: {
                200: z.object({
                    groups: z.array(consolidationGroupSchema),
                }),
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getGroup: {
            method: "GET",
            path: "/:workspaceId/groups/:id",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: z.object({
                    group: consolidationGroupSchema,
                }),
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getBasisOptions: {
            method: "POST",
            path: "/:workspaceId/groups/basis-options",
            pathParams: workspacePathParams,
            body: consolidationBasisOptionsBodySchema,
            responses: {
                200: consolidationBasisOptionsResultSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        checkEligibility: {
            method: "POST",
            path: "/:workspaceId/groups/eligibility",
            pathParams: workspacePathParams,
            body: consolidationEligibilityBodySchema,
            responses: {
                200: consolidationEligibilityResultSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createGroup: {
            method: "POST",
            path: "/:workspaceId/groups",
            pathParams: workspacePathParams,
            body: createConsolidationGroupBodySchema,
            responses: {
                201: z.object({
                    group: consolidationGroupSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        updateGroupConfig: {
            method: "PATCH",
            path: "/:workspaceId/groups/:id/config",
            pathParams: workspaceResourcePathParams,
            body: updateConsolidationGroupConfigBodySchema,
            responses: {
                200: z.object({
                    group: consolidationGroupSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateGroupMember: {
            method: "PATCH",
            path: "/:workspaceId/groups/:id/members/:memberId",
            pathParams: workspaceResourcePathParams.extend({
                memberId: z.string().uuid(),
            }),
            body: updateConsolidationGroupMemberBodySchema,
            responses: {
                200: z.object({
                    member: consolidationGroupMemberSchema,
                }),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        dissolveGroup: {
            method: "DELETE",
            path: "/:workspaceId/groups/:id",
            pathParams: workspaceResourcePathParams,
            responses: {
                200: z.void(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
        },
        listEliminatingEntries: {
            method: "GET",
            path: "/:workspaceId/groups/:groupId/eliminating-entries",
            pathParams: groupEliminatingEntryPathParams,
            responses: {
                200: listEliminatingEntriesResponseSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        createEliminatingEntry: {
            method: "POST",
            path: "/:workspaceId/groups/:groupId/eliminating-entries",
            pathParams: groupEliminatingEntryPathParams,
            body: createEliminatingEntryBodySchema,
            responses: {
                201: eliminatingEntryMutationResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateEliminatingEntry: {
            method: "PATCH",
            path: "/:workspaceId/groups/:groupId/eliminating-entries/:id",
            pathParams: groupEliminatingEntryResourcePathParams,
            body: updateEliminatingEntryBodySchema,
            responses: {
                200: eliminatingEntryMutationResponseSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteEliminatingEntry: {
            method: "DELETE",
            path: "/:workspaceId/groups/:groupId/eliminating-entries/:id",
            pathParams: groupEliminatingEntryResourcePathParams,
            responses: {
                200: z.void(),
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        getCanvasLayout: {
            method: "GET",
            path: "/:workspaceId/canvas-layout",
            pathParams: workspacePathParams,
            responses: {
                200: consolidationCanvasLayoutSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateCanvasLayout: {
            method: "PUT",
            path: "/:workspaceId/canvas-layout",
            pathParams: workspacePathParams,
            body: updateConsolidationCanvasLayoutBodySchema,
            responses: {
                200: consolidationCanvasLayoutSchema,
                400: ErrorMessageSchema,
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        resetCanvasLayout: {
            method: "DELETE",
            path: "/:workspaceId/canvas-layout",
            pathParams: workspacePathParams,
            responses: {
                200: z.void(),
                401: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/consolidation",
    },
);
